import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Tooltip from '../ui/Tooltip';
import { useSyncStatusStore } from '../../store/syncStatusStore';
import { recordsContext } from '../../extensions/bundled/documents/documentsStore';
import { isWebApp } from '../../platform/target';
import { shortAge } from './shortAge';
import { retrySync } from './syncRetry';

/**
 * What the footer says for one bucket's sync entry (the engine's own
 * `sync:status`, via syncStatusStore — the same source as the titlebar's sync
 * indicator). `tone` colours the dot: ok / waiting / error / busy / local.
 */
export function describeDocsSync({ entry, signedIn, scopeType, web, now, t }) {
  if (!signedIn || scopeType === 'device') return { tone: 'local', label: t('docSidebar.sync.local') };
  if (web) return { tone: 'ok', label: t('docSidebar.sync.cloud') };
  if (!entry) return { tone: 'idle', label: t('docSidebar.sync.notYet') };
  const pending = Number(entry.pending) || 0;
  if (typeof entry.lastError === 'string' && entry.lastError.includes('upgrade_required')) {
    return { tone: 'local', label: t('docSidebar.sync.local') };
  }
  if (entry.state === 'offline') {
    return { tone: 'waiting', label: pending ? t('docSidebar.sync.waitingOffline', { count: pending }) : t('docSidebar.sync.offline') };
  }
  if (entry.state === 'auth_error' || entry.lastError) {
    return { tone: 'error', label: t('docSidebar.sync.failed'), error: entry.lastError || entry.state, retry: true };
  }
  if (entry.state === 'pushing' || entry.state === 'pulling') return { tone: 'busy', label: t('docSidebar.sync.syncing') };
  if (pending) return { tone: 'waiting', label: t('docSidebar.sync.waiting', { count: pending }) };
  if (!entry.lastOkAt) return { tone: 'ok', label: t('docSidebar.sync.synced') };
  if (now - entry.lastOkAt < 60_000) return { tone: 'ok', label: t('docSidebar.sync.syncedJustNow') };
  return { tone: 'ok', label: t('docSidebar.sync.syncedAgo', { ago: shortAge(entry.lastOkAt, now, t) }) };
}

/**
 * The sidebar's 36 px footer (card 1.8): a status dot and the sync state of
 * the tree on show — "Synced · 4 d ago", "3 changes waiting (offline)", or
 * "Sync failed" + Retry. No app version here (owner, 14.10.2026): the window
 * footer already shows it.
 */
export default function SidebarFooter({ scope, signedIn }) {
  const { t } = useTranslation();
  const workspaceId = recordsContext(scope).workspaceId;
  const entry = useSyncStatusStore((s) => s.byWorkspace[workspaceId]);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(tick);
  }, []);

  useEffect(() => setNow(Date.now()), [entry]);

  const sync = describeDocsSync({ entry, signedIn, scopeType: scope?.type, web: isWebApp(), now, t });
  const status = (
    <span className="doc-footer-status" role="status">
      <span className={`doc-footer-dot doc-footer-dot--${sync.tone}`} aria-hidden />
      <span className="doc-footer-label">{sync.label}</span>
    </span>
  );

  return (
    <div className="doc-sidebar-footer" data-testid="docs-sync-footer" data-tone={sync.tone}>
      {sync.error ? <Tooltip content={String(sync.error)} side="top">{status}</Tooltip> : status}
      {sync.retry && (
        <button type="button" className="doc-footer-retry" onClick={() => retrySync(workspaceId)}>
          {t('docSidebar.sync.retry')}
        </button>
      )}
    </div>
  );
}
